import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Save, RotateCcw, Upload, Plus, Trash2, Eye, EyeOff, Megaphone, Image as ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = { withCredentials: true };

const SECTION_META = {
  hero: { title: 'Hero Section', hint: 'Main headline, supporting text and hero image.' },
  hero_cta: { title: 'Hero Buttons & Quick Post', hint: 'Button labels and where each one links to.' },
  how: { title: 'How JustDeliver Works', hint: 'Heading and sub-heading for the steps section.' },
  trust: { title: 'Trust Row', hint: 'Eyebrow label and heading above the trust icons.' },
  columns: { title: 'Customers / Drivers Columns', hint: 'The two side-by-side panels.' },
  vehicles: { title: 'Vehicles Section', hint: 'Heading and closing line above the final call to action.' },
  final: { title: 'Final Call To Action', hint: 'The black band at the bottom of the homepage.' },
};

const LABELS = {
  hero_headline_line1: 'Headline — line 1',
  hero_headline_line2: 'Headline — line 2 (green)',
  hero_subheadline: 'Subheadline',
  hero_text: 'Supporting text',
  hero_image_url: 'Hero image',
  hero_image_caption: 'Image caption chip',
  hero_cta_primary_text: 'Primary button text',
  hero_cta_primary_link: 'Primary button link',
  hero_cta_secondary_text: 'Secondary button text',
  hero_cta_secondary_link: 'Secondary button link',
  hero_quickpost_title: 'Quick Post title',
  hero_quickpost_text: 'Quick Post small print',
  hero_quickpost_link: 'Quick Post link',
  how_title_prefix: 'Heading — before brand',
  how_title_brand: 'Heading — brand word',
  how_title_suffix: 'Heading — after brand',
  how_subtitle: 'Sub-heading',
  trust_eyebrow: 'Eyebrow label',
  trust_title: 'Heading',
  customers_eyebrow: 'Customers — eyebrow',
  customers_title: 'Customers — heading',
  drivers_eyebrow: 'Drivers — eyebrow',
  drivers_title: 'Drivers — heading',
  vehicles_eyebrow: 'Eyebrow label',
  vehicles_title: 'Heading',
  vehicles_footnote: 'Closing line',
  final_title: 'Heading',
  final_text: 'Supporting text',
  final_cta_primary_text: 'Primary button text',
  final_cta_primary_link: 'Primary button link',
  final_cta_secondary_text: 'Secondary button text',
  final_cta_secondary_link: 'Secondary button link',
};

const EMPTY_BANNER = { message: '', link_text: '', link_url: '', style: 'info', active: true, starts_at: '', ends_at: '' };

export default function HomepageCMSPanel() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState({});
  const [defaults, setDefaults] = useState({});
  const [sections, setSections] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [banners, setBanners] = useState([]);
  const [newBanner, setNewBanner] = useState(EMPTY_BANNER);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [c, b] = await Promise.all([
        axios.get(`${API}/admin/content/homepage`, auth),
        axios.get(`${API}/admin/content/banners`, auth),
      ]);
      setContent(c.data.content || {});
      setDefaults(c.data.defaults || {});
      setSections(c.data.sections || {});
      setBanners(b.data.banners || []);
    } catch (err) {
      toast.error('Failed to load homepage content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (key, value) => setContent((c) => ({ ...c, [key]: value }));

  const saveSection = async (section) => {
    setSavingSection(section);
    try {
      const keys = sections[section] || [];
      const updates = Object.fromEntries(keys.map((k) => [k, content[k] ?? '']));
      const { data } = await axios.put(`${API}/admin/content/homepage`, { updates }, auth);
      setContent(data.content);
      toast.success('Saved — the homepage is updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingSection(null);
    }
  };

  const resetSection = async (section) => {
    setSavingSection(section);
    try {
      const { data } = await axios.delete(`${API}/admin/content/homepage/${section}`, auth);
      setContent(data.content);
      toast.success('Section reset to the original wording');
    } catch (err) {
      toast.error('Failed to reset');
    } finally {
      setSavingSection(null);
    }
  };

  const uploadImage = async (file, key) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/content/upload`, form, {
        ...auth,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setField(key, data.url);
      toast.success('Image uploaded — press Save to publish it');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const createBanner = async () => {
    if (!newBanner.message.trim()) {
      toast.error('Add a message for the banner');
      return;
    }
    try {
      await axios.post(`${API}/admin/content/banners`, newBanner, auth);
      setNewBanner(EMPTY_BANNER);
      toast.success('Banner published');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create banner');
    }
  };

  const updateBanner = async (b, changes) => {
    try {
      await axios.put(`${API}/admin/content/banners/${b.id}`, { ...b, ...changes }, auth);
      load();
    } catch (err) {
      toast.error('Failed to update banner');
    }
  };

  const deleteBanner = async (id) => {
    try {
      await axios.delete(`${API}/admin/content/banners/${id}`, auth);
      toast.success('Banner deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-10" data-testid="cms-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading homepage content…
      </div>
    );
  }

  const resolveUrl = (u) => (u && u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <div className="space-y-6" data-testid="homepage-cms-panel">
      <div className="p-4 border bg-blue-50/60 border-blue-200">
        <p className="text-sm text-blue-900 font-semibold">Changes publish instantly</p>
        <p className="text-xs text-blue-800/80 mt-0.5">
          Press Save in a section and the live homepage updates straight away — no redeploy needed. Use Reset to restore the original wording.
        </p>
      </div>

      {/* Promo banners */}
      <div className="border bg-white" data-testid="cms-banners">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-bold text-gray-900">Promotional Banners &amp; Announcements</h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 border border-dashed bg-gray-50/60">
            <div className="md:col-span-4 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">Message</Label>
              <Input value={newBanner.message} onChange={(e) => setNewBanner({ ...newBanner, message: e.target.value })} placeholder="e.g. Free posting all June" data-testid="cms-new-banner-message" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">Link text</Label>
              <Input value={newBanner.link_text} onChange={(e) => setNewBanner({ ...newBanner, link_text: e.target.value })} placeholder="Learn more" data-testid="cms-new-banner-link-text" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">Link URL</Label>
              <Input value={newBanner.link_url} onChange={(e) => setNewBanner({ ...newBanner, link_url: e.target.value })} placeholder="/quick-post" data-testid="cms-new-banner-link-url" />
            </div>
            <div className="md:col-span-1 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">Style</Label>
              <select value={newBanner.style} onChange={(e) => setNewBanner({ ...newBanner, style: e.target.value })} className="w-full border border-gray-200 rounded px-2 py-2 text-sm" data-testid="cms-new-banner-style">
                <option value="info">Blue</option>
                <option value="success">Green</option>
                <option value="warning">Amber</option>
                <option value="dark">Black</option>
              </select>
            </div>
            <div className="md:col-span-1 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">From</Label>
              <Input type="date" value={newBanner.starts_at} onChange={(e) => setNewBanner({ ...newBanner, starts_at: e.target.value })} data-testid="cms-new-banner-start" />
            </div>
            <div className="md:col-span-1 space-y-1">
              <Label className="text-[11px] font-semibold text-gray-600">To</Label>
              <Input type="date" value={newBanner.ends_at} onChange={(e) => setNewBanner({ ...newBanner, ends_at: e.target.value })} data-testid="cms-new-banner-end" />
            </div>
            <div className="md:col-span-1">
              <Button onClick={createBanner} className="w-full bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-1" data-testid="cms-add-banner-btn">
                <Plus className="w-4 h-4" /> Add
              </Button>
            </div>
          </div>

          {banners.length === 0 ? (
            <p className="text-xs text-gray-400 italic" data-testid="cms-no-banners">No banners yet. Add one above and it appears at the top of the homepage.</p>
          ) : (
            <div className="space-y-2">
              {banners.map((b) => (
                <div key={b.id} className="flex items-center gap-3 p-3 border bg-white" data-testid={`cms-banner-row-${b.id}`}>
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${b.active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{b.message}</p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {b.style} · {b.link_text ? `${b.link_text} → ${b.link_url}` : 'no link'}
                      {(b.starts_at || b.ends_at) && ` · ${b.starts_at || '…'} to ${b.ends_at || '…'}`}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => updateBanner(b, { active: !b.active })} className="gap-1" data-testid={`cms-banner-toggle-${b.id}`}>
                    {b.active ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Show</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteBanner(b.id)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`cms-banner-delete-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content sections */}
      {Object.entries(sections).map(([section, keys]) => (
        <div key={section} className="border bg-white" data-testid={`cms-section-${section}`}>
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-bold text-gray-900">{SECTION_META[section]?.title || section}</h3>
              <p className="text-[11px] text-gray-500">{SECTION_META[section]?.hint}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => resetSection(section)} disabled={savingSection === section} className="gap-1" data-testid={`cms-reset-${section}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Reset
              </Button>
              <Button size="sm" onClick={() => saveSection(section)} disabled={savingSection === section} className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] gap-1" data-testid={`cms-save-${section}`}>
                {savingSection === section ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </Button>
            </div>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {keys.map((key) => (
              key === 'hero_image_url' ? (
                <div key={key} className="md:col-span-2 space-y-2">
                  <Label className="text-[11px] font-semibold text-gray-600">{LABELS[key] || key}</Label>
                  <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="w-40 h-24 border bg-gray-50 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {content[key] ? (
                        <img src={resolveUrl(content[key])} alt="Hero preview" className="w-full h-full object-cover" data-testid="cms-hero-preview" />
                      ) : <ImageIcon className="w-6 h-6 text-gray-300" />}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <Input value={content[key] || ''} onChange={(e) => setField(key, e.target.value)} placeholder="Image URL" data-testid={`cms-field-${key}`} />
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], key)} data-testid="cms-hero-file-input" />
                      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1" data-testid="cms-hero-upload-btn">
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload image
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={key} className="space-y-1">
                  <Label className="text-[11px] font-semibold text-gray-600">{LABELS[key] || key}</Label>
                  {(content[key] || '').length > 70 ? (
                    <textarea
                      value={content[key] || ''}
                      onChange={(e) => setField(key, e.target.value)}
                      rows={3}
                      className="w-full text-sm border border-gray-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30"
                      data-testid={`cms-field-${key}`}
                    />
                  ) : (
                    <Input value={content[key] || ''} onChange={(e) => setField(key, e.target.value)} data-testid={`cms-field-${key}`} />
                  )}
                  {defaults[key] && content[key] !== defaults[key] && (
                    <p className="text-[10px] text-gray-400 truncate" title={defaults[key]}>Default: {defaults[key]}</p>
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
